import React from 'react'
import { css } from '@emotion/core'
import { Tabs, Tab, useAtSize } from '@fs/zion-ui'
import { arrayOf, shape } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useShowTemple } from '@fs/zion-permissions'
import useDSApplicationMode from '../../../../hooks/useDSApplicationMode'
import PersonPhotos from './PersonPhotos/PersonPhotos'
import PersonStories from './PersonStories/PersonStories'
import PersonTimeline from './PersonTimeline/PersonTimeline'
import PersonAudio from './PersonAudio/PersonAudio'
import PersonOrdinances from './PersonOrdinances/PersonOrdinances'
import PersonDocuments from './PersonDocuments/PersonDocuments'
import PersonRelationship from './PersonRelationship/PersonRelationship'
import { PersonType } from '../../../../propTypes/personDataProps'
import { usePersonViewerState } from '../../state/PersonViewerStore'

const TabList = ({ activeTab, setActiveTab, showTemple, facts, tree }) => {
  const [t] = useTranslation()
  const [selectedTab, setSelectedTab] = React.useState(0)
  const atSize = useAtSize()
  const treeAtSizeDefault = tree
    ? { id: 'Relationship', title: t('person-details.relationship', 'Relationship') }
    : 'hide'

  let tabsData = [
    facts && facts.length ? { id: 'Timeline', title: t('person-details.timeline', 'Timeline') } : 'hide',
    atSize({ default: treeAtSizeDefault, xl: 'hide' }),
    { id: 'Photos', title: t('person-details.photos', 'Photos') },
    { id: 'Stories', title: t('person-details.stories', 'Stories') },
    { id: 'Documents', title: t('person-details.documents', 'Documents') },
    { id: 'Audio', title: t('person-details.audio', 'Audio') },
  ]

  // Zion-UI doesn't provide a way to remove the tabs at certain sizes, so we have to filter that tab out here
  tabsData = tabsData.filter((tab) => tab !== 'hide')

  if (showTemple) {
    tabsData.push({ id: 'Ordinances', title: t('person-details.ordinances', 'Ordinances') })
  }

  React.useEffect(() => {
    const currentTab = tabsData.map((tab) => tab.id).indexOf(activeTab.id)
    atSize({
      default:
        activeTab.id === 'Relationship' && tabsData.length === 7 ? setSelectedTab(1) : setSelectedTab(currentTab),
      xl: activeTab.id === 'Relationship' && tabsData.length === 6 ? setSelectedTab(0) : setSelectedTab(currentTab),
    })
  }, [activeTab.id, atSize, tabsData, tabsData.length])

  return (
    <Tabs
      index={selectedTab}
      elevated={false}
      onChange={(index) => {
        setActiveTab(tabsData[index])
        setSelectedTab(index)
      }}
      fullWidth
    >
      {tabsData.map((tabData) => (
        <Tab key={tabData.id} title={tabData.title} />
      ))}
    </Tabs>
  )
}

const TabContent = ({ personData, tree, showTemple, activeTab, initialPerson, onPersonClick }) => {
  const personViewerState = usePersonViewerState()
  const appMode = useDSApplicationMode()
  const atSize = useAtSize()
  const memoryProps = {
    ...personData,
    centerOrStation: appMode === 'KIOSK' || appMode === 'STATION',
  }
  const tabContentCSS = css`
    overflow-y: ${personViewerState.tabContentOverflow};
    margin-top: 10px;
    -webkit-overflow-scrolling: touch;
    flex-grow: 1;
  `
  return (
    <div css={tabContentCSS}>
      {activeTab.id === 'Timeline' && (
        <PersonTimeline
          personData={personData}
          tree={tree}
          initialPerson={initialPerson}
          onPersonClick={onPersonClick}
        />
      )}
      {atSize({
        default: activeTab.id === 'Relationship' && (
          <PersonRelationship
            personData={personData}
            tree={tree}
            initialPerson={initialPerson}
            onPersonClick={onPersonClick}
          />
        ),
        xl: activeTab.id === 'Relationship' && (
          <PersonTimeline
            personData={personData}
            tree={tree}
            initialPerson={initialPerson}
            onPersonClick={onPersonClick}
          />
        ),
      })}
      {activeTab.id === 'Photos' && <PersonPhotos {...memoryProps} />}
      {activeTab.id === 'Stories' && <PersonStories {...memoryProps} />}
      {activeTab.id === 'Documents' && <PersonDocuments {...memoryProps} />}
      {activeTab.id === 'Audio' && <PersonAudio {...memoryProps} />}
      {showTemple && activeTab.id === 'Ordinances' && <PersonOrdinances personId={personData.personId} />}
    </div>
  )
}

export default function PersonTabs({ personData, tree, initialPerson, onPersonClick }) {
  const { facts } = personData
  const showTemple = useShowTemple()
  const atSize = useAtSize()
  const [t] = useTranslation()
  const initialActiveTab =
    facts && facts.length
      ? { id: 'Timeline', title: t('person-details.timeline', 'Timeline') }
      : { id: 'Photos', title: t('person-details.photos', 'Photos') }
  const [activeTab, setActiveTab] = React.useState(initialActiveTab)
  const listProps = {
    personData,
    activeTab,
    setActiveTab,
    showTemple,
    facts,
    tree,
  }

  const contentProps = {
    ...listProps,
    initialPerson,
    onPersonClick,
  }

  const largeContainerCSS = css`
    z-index: 1;
    width: 100%;
    max-width: 800px;
    align-self: center;
  `

  return (
    <>
      <div css={atSize({ default: null, lg: largeContainerCSS })}>
        <TabList {...listProps} />
      </div>
      <TabContent {...contentProps} />
    </>
  )
}

PersonTabs.propTypes = {
  personData: PersonType,
  tree: arrayOf(shape({})),
}
